import { DocumentNode } from 'apollo-link';

import { DataCatalogApolloClient } from '../apolloClientGenerator';
import { QueryRows } from '../dataGrid/PaginatedRowStore';
import { TypedRow } from '../dataGrid/TypedDataGridInterfaces';
import { GraphQLError } from './GraphQLError';
import { ItemWithId } from './ItemWithId';

/** The subset of variables that must exist on paginated GraphQL queries */
export interface PaginationQueryVariables {
  readonly offset?: number | null;
  readonly first?: number | null;
}

export interface ConnectionListResult<TListNode extends ItemWithId> {
  readonly totalCount: number;
  readonly nodes: TListNode[];
}

export interface SliceResult<TResultItem extends ItemWithId> {
  readonly totalCount: number;
  readonly items: TResultItem[];
  readonly errors?: ReadonlyArray<GraphQLError>;
}

export interface Sliceable<TResultItem extends ItemWithId> {
  readonly getSlice: (sliceBoundaries: { start: number, size: number }) => Promise<SliceResult<TResultItem>>;
}

/**
 * The type parameter specifies the full query variables to ensure that the query actually supports the
 * pagination variables needed. However, since the wrapper injects the pagination variables as each
 * page request is made, only the other variables need to be supplied from the parent component.
 */
export type PartialQueryVariables<T> = Omit<T, keyof PaginationQueryVariables>;


export interface GraphQLSliceableParams<
  TQueryResults,
  TQueryResultListNode extends ItemWithId,
  TResultItem extends ItemWithId,
  > {

  /**
   * Due to an "any" in the Apollo typedefs, the GQL is inherently type-unsafe. The best we can do
   * is to define the trio of (query, query variables, query result) as close to each other as possible
   * to make sure they're all put together correctly. We do that in each instantiation of the wrapper
   * component, when we declare the type parameters and props for each particular instance.
   */
  readonly queryGql: DocumentNode | string;

  readonly selectConnectionListResult: (results: TQueryResults) => ConnectionListResult<TQueryResultListNode> | null | undefined;
  readonly mapResultNode: (node: TQueryResultListNode, resultData: TQueryResults) => Readonly<TResultItem>;
}


/**
 * The GraphQLSliceable is used by the VizPortal explorers and the DC3 grids to make paginated GraphQL queries.
 */
export class GraphQLSliceable<
  TQueryResultListNode extends ItemWithId,
  TResultItem extends ItemWithId,
  TQueryVariables extends PaginationQueryVariables,
  TQueryResults
  > implements Sliceable<TResultItem> {

  public constructor(
    private params: GraphQLSliceableParams<TQueryResults, TQueryResultListNode, TResultItem>,
    private partialQueryVariables: PartialQueryVariables<TQueryVariables>,
    private client: DataCatalogApolloClient,
  ) { }

  private getResultRows(
    listResult: ConnectionListResult<TQueryResultListNode> | null | undefined,
    resultData: TQueryResults
  ): TResultItem[] {

    if (listResult && listResult.nodes && listResult.totalCount !== null) {
      return listResult.nodes.map(node => this.params.mapResultNode(node, resultData));
    } else {
      return [];
    }
  }

  private getTotalCount(listResult: ConnectionListResult<TQueryResultListNode> | null | undefined): number {
    return listResult ? listResult.totalCount : 0;
  }

  public async getSlice(sliceBoundaries: { start: number, size: number }): Promise<SliceResult<TResultItem>> {
    const variables = {
      ...this.partialQueryVariables,
      offset: sliceBoundaries.start,
      first: sliceBoundaries.size,
    } as TQueryVariables;

    const apolloQueryResult = await this.client.query<TQueryResults, TQueryVariables & PaginationQueryVariables>({
      // TODO: remove this cast when updating apollo dependencies (TFS 944332)
      // This cast is due to the sloppy nature of some of the apollo types not properly addressing the fact that
      // string values are what are being passed as DocumentNodes which runs perfectly fine until we add graphql types
      // that specify typing on document nodes.
      query: this.params.queryGql as DocumentNode,
      variables,
    });

    const resultData = apolloQueryResult.data;
    const listResult = this.params.selectConnectionListResult(resultData);

    return {
      items: this.getResultRows(listResult, resultData),
      totalCount: this.getTotalCount(listResult),
      // TODO: Revisit the use of this cast when updating graphql/apollo dependencies (types) -- TFS 944332
      // The following dangerous cast SHOULD be safe so long as the locally GraphQLError type stays in sync
      // with the types of errors returned by the relationship service
      errors: apolloQueryResult.errors as unknown as GraphQLError[] | undefined,
    };
  }

}


const defaultBlockSize = 1000;

export async function getAllFromSliceable<TResultItem extends ItemWithId>(
  sliceable: Sliceable<TResultItem>,
  blockSize: number = defaultBlockSize
): Promise<ReadonlyArray<TResultItem>> {
  let items: TResultItem[] = [];
  let itemsRemaining: number | null = null;
  while (itemsRemaining === null || itemsRemaining > 0) {
    const slice = await sliceable.getSlice({ start: items.length, size: blockSize });
    items = items.concat(slice.items);
    itemsRemaining = slice.totalCount - items.length;
  }
  return items;
}


function makeRowsForItems<TResultItem extends ItemWithId>(items: ReadonlyArray<TResultItem>): Readonly<TypedRow<TResultItem>>[] {
  return items.map(item => ({
    key: item.id,
    data: item,
  }));
}


/**
 * For the Data Catalog grids, we transform the sliceable result into data grid rows.
 * The VizPortal Client explorer code has a different mechanism for doing this transformation.
 */
export function makeRowFetcher<
  TQueryVariables extends PaginationQueryVariables,
  TQueryResults,
  TQueryResultListNode extends ItemWithId,
  TResultItem extends ItemWithId
>(
  sliceableParams: GraphQLSliceableParams<TQueryResults, TQueryResultListNode, TResultItem>,
  partialQueryVariables: PartialQueryVariables<TQueryVariables>,
  client: DataCatalogApolloClient,
): QueryRows<TResultItem, GraphQLError> {

  const sliceable = new GraphQLSliceable(sliceableParams, partialQueryVariables, client);

  return async function queryRows(pageBoundaries: { start: number, end: number }) {
    const { start, end } = pageBoundaries;
    const slice = await sliceable.getSlice({ start, size: end - start + 1 });
    return {
      rows: makeRowsForItems(slice.items),
      totalCount: slice.totalCount,
      errors: slice.errors,
    };
  };
}
